import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin } from 'react-icons/fa';
import { CONTACT_INFO } from '../constants/contact';

const ContactSection = styled.section`
  padding: 110px 0 64px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.25rem, 5vw, 3.25rem);
  font-weight: 800;
  margin-bottom: 3.75rem;
  background: linear-gradient(135deg, #ffffff 0%, #9beeff 58%, #7affc8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.7rem;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #7affc8, rgba(103, 216, 255, 0));
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfo = styled(motion.div)``;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.052);
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.075);
    transform: translateX(6px);
  }
`;

const ContactIcon = styled.div`
  font-size: 1.5rem;
  color: #7affc8;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(122, 255, 200, 0.1);
  border-radius: 8px;
`;

const ContactText = styled.div``;

const ContactLabel = styled.h4`
  color: #ffffff;
  font-size: 1rem;
  margin-bottom: 0.3rem;
`;

const ContactValue = styled.p`
  color: #cccccc;
  font-size: 0.9rem;
`;

const ContactForm = styled(motion.form)`
  background: rgba(255, 255, 255, 0.052);
  border: 1px solid rgba(255, 255, 255, 0.095);
  border-radius: 8px;
  padding: 2rem;
`;

const FormTitle = styled.h3`
  color: #ffffff;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  color: #cccccc;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #7affc8;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: #999999;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #7affc8;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: #999999;
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, #7affc8, #67d8ff);
  color: #061016;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 38px rgba(122, 255, 200, 0.18);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: 1px solid rgba(128, 230, 255, 0.3);
  border-radius: 8px;
  color: #9beeff;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(128, 230, 255, 0.14);
    color: #ffffff;
    transform: translateY(-3px);
  }
`;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // 이메일로 직접 전송
    const mailtoLink = `mailto:${CONTACT_INFO.EMAIL}?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(`이름: ${formData.name}\n이메일: ${formData.email}\n\n메시지:\n${formData.message}`)}`;
    
    // 새 창에서 이메일 클라이언트 열기
    window.open(mailtoLink, '_blank');
    
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
      alert('이메일 클라이언트가 열렸습니다. 메시지를 확인하고 전송해주세요!');
    }, 1000);
  };

  return (
    <ContactSection id="contact">
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        Contact Me
      </SectionTitle>
      
      <ContactContainer>
        <ContactInfo
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <ContactItem>
            <ContactIcon>
              <FaEnvelope />
            </ContactIcon>
            <ContactText>
              <ContactLabel>Email</ContactLabel>
              <ContactValue>{CONTACT_INFO.EMAIL}</ContactValue>
            </ContactText>
          </ContactItem>
          
          <ContactItem>
            <ContactIcon>
              <FaPhone />
            </ContactIcon>
            <ContactText>
              <ContactLabel>Phone</ContactLabel>
              <ContactValue>{CONTACT_INFO.PHONE}</ContactValue>
            </ContactText>
          </ContactItem>
          
          <ContactItem>
            <ContactIcon>
              <FaMapMarkerAlt />
            </ContactIcon>
            <ContactText>
              <ContactLabel>Location</ContactLabel>
              <ContactValue>{CONTACT_INFO.LOCATION}</ContactValue>
            </ContactText>
          </ContactItem>
          
          <SocialLinks>
            <SocialLink href={CONTACT_INFO.GITHUB} target="_blank" rel="noopener noreferrer">
              <FaGithub />
            </SocialLink>
            <SocialLink href={CONTACT_INFO.LINKEDIN} target="_blank" rel="noopener noreferrer">
              <FaLinkedin />
            </SocialLink>
          </SocialLinks>
        </ContactInfo>
        
        <ContactForm
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
        >
          <FormTitle>메시지 보내기</FormTitle>
          
          <FormGroup>
            <FormLabel htmlFor="name">이름</FormLabel>
            <FormInput
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="이름을 입력하세요"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="email">이메일</FormLabel>
            <FormInput
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="이메일을 입력하세요"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="subject">제목</FormLabel>
            <FormInput
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="제목을 입력하세요"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="message">메시지</FormLabel>
            <FormTextarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="메시지를 입력하세요"
              required
            />
          </FormGroup>
          
          <SubmitButton
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isSubmitting ? '전송 중...' : '메시지 보내기'}
          </SubmitButton>
        </ContactForm>
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact; 
